import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Mail, Loader2, CheckCircle, AlertCircle, Search, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { authAPI } from '../lib/api';

const RESEND_STATE_IDLE = 'idle';
const RESEND_STATE_LOADING = 'loading';
const RESEND_STATE_SUCCESS = 'success';
const RESEND_STATE_RATE_LIMITED = 'rate_limited';
const RESEND_STATE_ERROR = 'error';

export default function VerifyEmailPendingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const [resendState, setResendState] = useState(RESEND_STATE_IDLE);

  const handleResend = async () => {
    setResendState(RESEND_STATE_LOADING);
    try {
      await authAPI.resendVerification();
      setResendState(RESEND_STATE_SUCCESS);
    } catch (error) {
      if (error?.response?.status === 429) {
        setResendState(RESEND_STATE_RATE_LIMITED);
      } else {
        setResendState(RESEND_STATE_ERROR);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12"
      data-testid="verify-email-pending-page"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-signal-blue rounded-sm flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-slate-800">
            TLA<span className="text-military-gold">HOUSING</span>
          </span>
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-signal-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-signal-blue" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
              {t('verify_pending.title', 'Check your inbox')}
            </h1>
            <p className="text-slate-600 text-sm">
              {t(
                'verify_pending.body',
                'We sent a verification link to your email address. Open it and confirm to unlock bookings, listings, and your dashboard.',
              )}
            </p>
            {user?.email && (
              <p className="mt-3 text-sm font-medium text-slate-800" data-testid="verify-pending-email">
                {user.email}
              </p>
            )}
          </div>

          {resendState === RESEND_STATE_SUCCESS && (
            <div
              className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-sm mb-4"
              data-testid="resend-success"
            >
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <p className="text-emerald-600 text-sm">
                {t('verify_pending.resend_success', 'Verification email sent. Please check your inbox.')}
              </p>
            </div>
          )}

          {resendState === RESEND_STATE_RATE_LIMITED && (
            <div
              className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm mb-4"
              data-testid="resend-rate-limited"
            >
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-amber-600 text-sm">
                {t(
                  'verify_pending.resend_rate_limited',
                  'Too many requests. Please wait a little while before requesting another email.',
                )}
              </p>
            </div>
          )}

          {resendState === RESEND_STATE_ERROR && (
            <div
              className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm mb-4"
              data-testid="resend-error"
            >
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-500 text-sm">
                {t('verify_pending.resend_error', 'Could not send the email. Please try again shortly.')}
              </p>
            </div>
          )}

          <Button
            onClick={handleResend}
            disabled={resendState === RESEND_STATE_LOADING}
            className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-sm py-5"
            data-testid="resend-verification-button"
          >
            {resendState === RESEND_STATE_LOADING ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('verify_pending.resend_sending', 'Sending...')}
              </>
            ) : (
              t('verify_pending.resend_button', 'Resend verification email')
            )}
          </Button>

          <div className="mt-4 grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/listings')}
              className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="browse-listings-button"
            >
              <Search className="w-4 h-4 mr-2" />
              {t('verify_pending.browse_listings', 'Browse listings')}
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-slate-600 hover:text-slate-800"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('verify_pending.logout', 'Log out')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
